import { Game, mathRandomRng } from "@2048/game-core";
import type { Direction } from "@2048/game-core";

import type { HtmlActuator } from "./adapters/html-actuator";
import type { KeyboardInputManager } from "./adapters/keyboard-input-manager";
import type { LocalStorageManager } from "./adapters/local-storage-manager";
import { reachGoal } from "./analytics/yandex-metrika";

const SIZE = 4;

/**
 * Orchestrates classic play: wires input → engine → renderer/storage. This is
 * the original `GameManager` with the pure logic now living in `@2048/game-core`
 * and IO (rendering, storage, analytics) kept here. Behaviour is unchanged;
 * `bestTile` tracking is the one addition.
 */
export class GameController {
  private game: Game;

  constructor(
    private readonly input: KeyboardInputManager,
    private readonly actuator: HtmlActuator,
    private readonly storage: LocalStorageManager,
  ) {
    this.input.on("move", (direction) => this.move(direction));
    this.input.on("restart", () => this.restart());
    this.input.on("keepPlaying", () => this.keepPlaying());

    const previous = this.storage.getGameState();
    this.game = previous
      ? new Game(mathRandomRng, SIZE, previous)
      : new Game(mathRandomRng, SIZE);

    this.actuate();
  }

  private restart(): void {
    this.storage.clearGameState();
    this.actuator.continueGame();
    this.game = new Game(mathRandomRng, SIZE);
    this.actuate();
  }

  private keepPlaying(): void {
    this.game.continuePlaying();
    this.actuator.continueGame();
  }

  private move(direction: Direction): void {
    const result = this.game.move(direction);
    if (!result.moved) return;

    if (result.over) {
      reachGoal(result.won ? "player-won" : "player-lost");
    }

    this.actuate();
  }

  private actuate(): void {
    const { score, maxTile } = this.game;

    if (this.storage.getBestScore() < score) this.storage.setBestScore(score);
    if (this.storage.getBestTile() < maxTile) this.storage.setBestTile(maxTile);

    // Clear saved state only on a loss; a win can be continued.
    if (this.game.over) {
      this.storage.clearGameState();
    } else {
      this.storage.setGameState(this.game.serialize());
    }

    this.actuator.actuate(this.game.grid, {
      score,
      bestScore: this.storage.getBestScore(),
      over: this.game.over,
      won: this.game.won,
      terminated: this.game.isTerminated(),
    });
  }
}
